import { listRegions } from 'app/plugins/infrastructure/components/regions/actions'
import { regionsSelector } from 'app/plugins/infrastructure/components/regions/selectors'
import { RootState } from 'app/store'
import { setAmplitudeUser } from 'app/utils/tracking'
import DocumentMeta from 'core/components/DocumentMeta'
import { MessageTypes } from 'core/components/notifications/model'
import Progress from 'core/components/progress/Progress'
import SessionExpiryModal from 'core/components/SessionExpiryModal'
import { determineCurrentStack, loadRegionFeatures } from 'core/containers/AuthController'
import RedirectToSecondaryRegion from 'core/elements/header/GlobalRegionDropdown'
import useListAction from 'core/hooks/useListAction'
import pluginManager from 'core/plugins/pluginManager'
import { useToast } from 'core/providers/ToastProvider'
import { PreferencesState, preferencesStoreKey } from 'core/session/preferencesReducers'
import { sessionActions, SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { getRegionTerm, isInfraRegion, isOpenCloud } from 'core/utils/helpers'
import Watchdog from 'core/watchdog'
import sessionTimeoutCheck from 'core/watchdog/session-timeout'
import { getBlueprints, getHostNetworkConfigs } from 'openstack/components/infrastructure/actions'
import { keys, prop } from 'ramda'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import Bugsnag from 'utils/bugsnag'
import AppBanners from './AppBanners'

export const getUnderlayInfo = (blueprintSettings, hostConfigs) => {
  const underlayType = blueprintSettings?.virtualNetworking?.underlayType
  const vnidRange = blueprintSettings?.virtualNetworking?.vnidRange || ''
  const vniRanges = vnidRange
    .split(',')
    .map((range) => range.trim())
    .map((range) => {
      const parts = range.split(':')
      return parts[0] === parts[1] ? parts[1] : `${parts[0]}-${parts[1]}`
    })
  const firstHostConfig = hostConfigs?.[0]
  const tunnelPhysnet = firstHostConfig?.tunnelingInterface
  const labelKeys = keys(firstHostConfig?.networkLabels)
  const tunnelLabel = labelKeys?.find((key) => {
    return firstHostConfig?.networkLabels?.[key] === tunnelPhysnet
  })
  return {
    type: underlayType,
    label: tunnelLabel,
    ranges: vniRanges,
  }
}

const AuthContainer = () => {
  const { history, location } = useReactRouter()
  const dispatch = useDispatch()
  const showToast = useToast()
  const { prefs, updatePrefs } = useScopedPreferences()
  const { lastStack } = prefs
  const isOpenCloudProduct = isOpenCloud()
  const session = useSelector<RootState, SessionState>(prop(sessionStoreKey))
  const {
    userDetails: { id: userId, name, displayName, role },
    features,
    onboardingRedirectToUrl,
    activeRegion,
    loadingRegion,
  } = session
  // stack is the name of the plugin (ex. kubernetes, account, developer, theme)
  const [currentStack, setStack] = useState(
    determineCurrentStack(history.location, lastStack, isOpenCloudProduct),
  )
  const { global = {} } = useSelector(prop<string, PreferencesState>(preferencesStoreKey))
  const { featureFlags: globalFeatureFlags } = global
  const plugins = pluginManager.getPlugins()
  const currentPlugin = plugins[currentStack]

  const PluginFrame = currentPlugin.getFrame()

  // Load regions list - only after authentication is established
  const { loading: loadingRegionList, reload: reloadRegions } = useListAction(listRegions, {
    initialLoadingState: true,
    loadOnDemand: true, // Don't load automatically
  })
  const regionList = useSelector(regionsSelector)

  // Ensure userDetails is loaded before rendering child components
  // This guarantees isAdmin() and other session-dependent checks work correctly
  const isUserDetailsLoaded = useMemo(() => {
    return userId && role
  }, [userId, role])

  // Load regions only after authentication is established
  useEffect(() => {
    if (isUserDetailsLoaded && !regionList?.length) {
      reloadRegions()
    }
  }, [isUserDetailsLoaded, regionList, reloadRegions])

  // When only Infra region is available
  const isSingleRegionDeployment = useMemo(() => regionList?.length === 1, [regionList])

  useEffect(() => {
    // targetPath is set in LoginPage.tsx and GlobalRegionDropdown.tsx.
    // This is used to redirect the user to the intended destination after login.
    const target = sessionStorage.getItem('targetPath')
    if (target) {
      sessionStorage.removeItem('targetPath')
      history.push(target)
    }
  }, [])

  const isLoadingInitialData = false
  const showOnboarding = false
  const regionTerm = getRegionTerm()

  useEffect(() => {
    async function loadBlueprintSettings() {
      if (activeRegion) {
        const blueprints = await getBlueprints()
        const blueprintSettings = blueprints?.[0]
        if (!blueprintSettings) {
          return
        }
        const virtualNetworking = !!blueprintSettings?.virtualNetworking?.enabled
        const hostConfigs = await getHostNetworkConfigs()
        const underlayNetwork = getUnderlayInfo(blueprintSettings, hostConfigs)
        dispatch(
          sessionActions.updateSession({
            virtualNetworking,
            underlayNetwork,
          }),
        )
      }
    }
    loadBlueprintSettings()
  }, [activeRegion, features])

  useEffect(() => {
    if (showOnboarding) return

    if (onboardingRedirectToUrl) {
      history.push(onboardingRedirectToUrl)
      dispatch(sessionActions.updateSession({ onboardingRedirectToUrl: null }))
    }
  }, [showOnboarding, globalFeatureFlags, onboardingRedirectToUrl, history])

  // Load features once on startup, other times will be handled by region selection
  useEffect(() => {
    const loadRegion = async () => {
      try {
        await loadRegionFeatures()
      } finally {
        dispatch(sessionActions.updateSession({ loadingRegion: false }))
      }
    }
    loadRegion()
  }, [])

  useEffect(() => {
    if (!location) {
      return
    }
    const newStack = determineCurrentStack(location, lastStack, isOpenCloudProduct)
    setStack(newStack)
    updatePrefs({ lastStack: newStack })
  }, [location, isOpenCloudProduct])

  useEffect(() => {
    setAmplitudeUser(userId)
    Bugsnag.setUser(userId, name, displayName)

    const cleanupCb = Watchdog.register({
      handler: sessionTimeoutCheck(showToast),
      frequency: 1000,
    })
    // Reset the interval if the session changes
    return () => {
      Bugsnag.setUser()
      cleanupCb()
    }
  }, [])

  // Add session expiration handling
  useEffect(() => {
    const handleSessionExpired = (event: CustomEvent) => {
      showToast(event?.detail?.message, MessageTypes.error, 3000, {
        vertical: 'top',
        horizontal: 'center',
      })
    }

    window.addEventListener('sessionExpired', handleSessionExpired as EventListener)

    return () => {
      window.removeEventListener('sessionExpired', handleSessionExpired as EventListener)
    }
  }, [showToast])

  // If the user logs in directly to the Infra page, redirect them to the region URL
  const shouldRedirectToRegionURL = useMemo(() => {
    if (loadingRegion || loadingRegionList) {
      return false
    }
    return isInfraRegion() && !isSingleRegionDeployment && !isOpenCloud()
  }, [loadingRegion, isInfraRegion, isSingleRegionDeployment, loadingRegionList])

  const isLoading =
    loadingRegion || isLoadingInitialData || shouldRedirectToRegionURL || !isUserDetailsLoaded

  return (
    <>
      <DocumentMeta title="Welcome" />
      {isLoading ? (
        <>
          <Progress
            loading
            message={
              shouldRedirectToRegionURL
                ? `Redirecting to ${regionTerm}...`
                : `Loading ${regionTerm} data...`
            }
          >
            {/* Redirect to secondary region if needed */}
            {shouldRedirectToRegionURL && <RedirectToSecondaryRegion hideDropdown={true} />}
          </Progress>
        </>
      ) : (
        <>
          <AppBanners />
          <PluginFrame currentPluginId={currentStack} features={features} role={role} />
        </>
      )}
      {/* Session Expiry Warning Modal */}
      <SessionExpiryModal />
    </>
  )
}

export default AuthContainer
